import { useEffect, useState } from 'react'
import _ from 'lodash'
import { Input } from '@design-systems/canvas-ui'
import RadioButtonGroup from '../../common/RadioButtonGroup'
import {
  FileHandler,
  FileDownloadOnly,
  CustomTooltip,
} from 'mvscommoncomponents'
import { exportMasterData, formatQuestionNumber } from '../../common/helper'
import {
  QUESTION_TYPE_UPLOAD,
  RFI_TYPE_QUESTIONNAIRE,
  QUESTION_TYPE_TEXTAREA,
  QUESTION_TYPE_TEXTBOX,
  SECTION_INLINE_HEADER,
  QUESTION_TYPE_PERCENTAGEBOX,
} from '../../common/commonConstants'
import {
  downloadFile,
  deleteFile,
  uploadFiles,
} from '../../services/lcmService'
import { showErrorMessage } from '../Builders'
import { BackgroundOverlay } from '../../common/BackgroundOverlay'
import Icon, { InfoIcon } from '@design-systems/react-icons'
import { isWithinPercentLimit } from 'mvscommoncomponents/lib/utils/commonUtils'

const RFIQuestion = (props) => {
  const [answerDetails, setAnswerDetails] = useState(null)
  const [filesSectionData, setFilesSectionData] = useState({
    uploaded_files: {},
  })
  //File Upload
  const [isFileActionInProgress, setIsFileActionInProgress] = useState(false)
  const [isFileActionFailed, setIsFileActionFailed] = useState(false)
  const [isShowOverlay, setIsShowOverlay] = useState(false)

  const errorMessage = props.errorCacheControl
    ? props.errorCacheControl[answerDetails?.uid]
    : ''

  //#region UseEffects

  useEffect(() => {
    if (!_.isEqual(answerDetails, props.answerDetails)) {
      setAnswerDetails(props.answerDetails)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.answerDetails])

  useEffect(() => {
    setFilesSectionData({
      uploaded_files: props.rfi3Files,
    })
  }, [props.rfi3Files])

  useEffect(() => {
    if (answerDetails && props.questionDetail) {
      let answerObject = {
        ...(answerDetails.answer && { answer: answerDetails.answer }),
        uid: props.questionDetail.uid,
        ...(answerDetails.note && { note: answerDetails.note }),
      }
      props.updateAnswer(answerObject)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerDetails])

  useEffect(() => {
    if (isShowOverlay) {
      setIsShowOverlay(false)
      props.updateRfi3Files(filesSectionData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesSectionData])

  //#region FileUploads

  /**
   * Upload RFI Files
   * @param {*} files
   * @returns
   */
  const uploadRfiFiles = async (files) => {
    try {
      setIsShowOverlay(true)
      setIsFileActionInProgress(true)
      setIsFileActionFailed(false)
      let response = await uploadFiles(
        RFI_TYPE_QUESTIONNAIRE,
        props.questionDetail?.uid,
        false,
        files,
        -1,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )
      setFilesSectionData({
        uploaded_files: {
          ...filesSectionData.uploaded_files,
          ...response.files,
        },
      })
      setIsFileActionInProgress(false)
      setIsFileActionFailed(false)
      return response
    } catch (error) {
      setIsFileActionInProgress(false)
      setIsFileActionFailed(true)
      setIsShowOverlay(false)
      return error
    }
  }

  /**
   * Download RFI file
   * @param {*} fileName
   * @param {*} tossKey
   * @returns
   */
  const downloadRfiFile = async (fileName, tossKey) => {
    try {
      setIsFileActionInProgress(true)
      setIsFileActionFailed(false)
      let response = await downloadFile(
        RFI_TYPE_QUESTIONNAIRE,
        props.questionDetail?.uid,
        fileName,
        tossKey,
        -1,
      )
      setIsFileActionInProgress(false)
      setIsFileActionFailed(false)
      return response
    } catch (error) {
      setIsFileActionInProgress(false)
      setIsFileActionFailed(true)
      return error
    }
  }

  /**
   * Delete RFI File
   * @param {*} fileName
   * @returns
   */
  const deleteRfiFile = async (fileName) => {
    try {
      setIsShowOverlay(true)
      setIsFileActionInProgress(true)
      setIsFileActionFailed(false)

      //1. Delete file
      let response = await deleteFile(
        RFI_TYPE_QUESTIONNAIRE,
        props.questionDetail?.uid,
        fileName,
        -1,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )
      // 2. Post delete success - update state
      let fileList = { ...filesSectionData.uploaded_files }
      delete fileList[fileName]
      setFilesSectionData({
        ...filesSectionData,
        uploaded_files: fileList,
      })
      setIsFileActionInProgress(false)
      setIsFileActionFailed(false)
      return response
    } catch (error) {
      setIsFileActionInProgress(false)
      setIsFileActionFailed(true)
      setIsShowOverlay(false)
      return error
    }
  }

  //#endregion

  return (
    <div className="hc8-ph-none hc8-pv-2x">
      {props.questionDetail?.type?.toLowerCase() === SECTION_INLINE_HEADER ? (
        <Input.Label>{props.questionDetail?.question}</Input.Label>
      ) : (
        <Input.Label
          className={[
            'hc8-fs-050',
            props.isReadOnly ? 'hc8-clr-placeholder' : '',
          ].join(' ')}
        >
          <div className="row-centre">
            {`${formatQuestionNumber(props.questionDetail?.order)}. ${props.questionDetail?.question}`}
            {props.questionDetail?.info_icon && (
              <CustomTooltip
                key={`tooltip`}
                placement="right"
                title={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: [props.questionDetail.info_icon],
                    }}
                  ></div>
                }
                arrow
                component={
                  <Icon
                    className="row-centre hc8-pl-2x hc8-clr-brand"
                    svg={InfoIcon}
                    aria-hidden
                  />
                }
              />
            )}
          </div>
        </Input.Label>
      )}

      {props.questionDetail?.options && (
        <div className="hc8-ml-6x">
          {props.isReadOnly ? (
            <Input.Label
              className="hc8-fs-100 hc8-mt-2x"
              key={`rfi-answer-rbg-${props.questionNumber}`}
            >
              {answerDetails?.answer}
            </Input.Label>
          ) : (
            <RadioButtonGroup
              key={`rfi-question-rbg-${props.questionNumber}`}
              id={`rfi-question-rbg-${props.questionNumber}`}
              value={answerDetails?.answer}
              options={exportMasterData(props.rfiMasterData, {
                nodeDotPath: props.questionDetail?.options,
              })}
              isReadOnly={props.isReadOnly}
              onOptionSelect={(value) => {
                setAnswerDetails({
                  ...answerDetails,
                  answer: value,
                })
              }}
            />
          )}
        </div>
      )}

      {(!props.questionDetail?.type ||
        props.questionDetail?.type?.toLowerCase() ===
          QUESTION_TYPE_TEXTAREA) && (
        <div className="hc8-ml-6x width-75pc">
          {props.isReadOnly ? (
            <Input.Label
              className="hc8-fs-100 hc8-mt-3x"
              key={`rfi-note-rbg-${props.questionNumber}`}
            >
              Notes: {answerDetails?.note}
            </Input.Label>
          ) : (
            <>
              <Input.Textarea
                data-testid={`rfi-question-note-${props.questionNumber}`}
                key={`rfi-question-note-${props.questionNumber}`}
                id={`rfi-question-note-${props.questionNumber}`}
                className="height-50p min-height-50p"
                value={answerDetails?.note ? answerDetails.note : ''}
                placeholder={'Add remarks / notes'}
                isErrored={!!errorMessage}
                isDisabled={props.isReadOnly}
                onChange={(event) => {
                  setAnswerDetails({
                    ...answerDetails,
                    note: event.target.value,
                  })
                }}
              />
              {showErrorMessage(errorMessage)}
            </>
          )}
        </div>
      )}

      {props.questionDetail?.type?.toLowerCase() === QUESTION_TYPE_TEXTBOX && (
        <div className="hc8-ml-6x width-75pc">
          {props.isReadOnly ? (
            <Input.Label
              className="hc8-fs-100 hc8-mt-3x"
              key={`rfi-answer-rbg-${props.questionNumber}`}
            >
              {answerDetails?.answer}
            </Input.Label>
          ) : (
            <>
              <Input.Text
                data-testid={`rfi-question-text-${props.questionNumber}`}
                key={`rfi-question-text-${props.questionNumber}`}
                id={`rfi-question-text-${props.questionNumber}`}
                value={answerDetails?.answer ? answerDetails.answer : ''}
                placeholder={'Text'}
                isErrored={!!errorMessage}
                isDisabled={props.isReadOnly}
                onChange={(event) => {
                  setAnswerDetails({
                    ...answerDetails,
                    answer: event.target.value,
                  })
                }}
              />
              {showErrorMessage(errorMessage)}
            </>
          )}
        </div>
      )}

      {props.questionDetail?.type?.toLowerCase() ===
        QUESTION_TYPE_PERCENTAGEBOX && (
        <div className="hc8-ml-6x width-75pc">
          {props.isReadOnly ? (
            <Input.Label
              className="hc8-fs-100 hc8-mt-3x"
              key={`rfi-answer-rbg-${props.questionNumber}`}
            >
              {answerDetails?.answer}
            </Input.Label>
          ) : (
            <>
              <Input.Text
                key={`rfi-question-perc-${props.questionNumber}`}
                id={`rfi-question-perc-${props.questionNumber}`}
                value={answerDetails?.answer ? answerDetails.answer : ''}
                isErrored={!!errorMessage}
                onChange={(event) => {
                  let newValue = event.target.value
                  var isValidNumber = isWithinPercentLimit(newValue)
                  newValue =
                    newValue === '' || isValidNumber
                      ? newValue
                      : answerDetails?.answer
                        ? answerDetails?.answer
                        : ''
                  setAnswerDetails({
                    ...answerDetails,
                    answer: newValue,
                  })
                }}
              />
            </>
          )}
        </div>
      )}

      {props.questionDetail?.type?.toLowerCase() === QUESTION_TYPE_UPLOAD && (
        <>
          {!props.isReadOnly ? (
            <div className="hc8-ml-6x hc8-mt-6x width-75pc">
              <FileHandler
                isFileActionInProgress={isFileActionInProgress}
                isFileActionFailed={isFileActionFailed}
                fileHandlerData={filesSectionData}
                uploadFiles={(files) => {
                  return uploadRfiFiles(files)
                }}
                downloadFiles={(fileName, tossKey) => {
                  return downloadRfiFile(fileName, tossKey)
                }}
                deleteFiles={(fileName) => deleteRfiFile(fileName)}
              />
            </div>
          ) : (
            <div className="hc8-ml-6x">
              <FileDownloadOnly
                fileHandlerData={{
                  uploadedFiles: filesSectionData?.uploaded_files,
                }}
                downloadFiles={(fileName, tossKey) => {
                  return downloadRfiFile(fileName, tossKey)
                }}
              />
            </div>
          )}
        </>
      )}

      {isShowOverlay && <BackgroundOverlay showSpinner />}
    </div>
  )
}

export default RFIQuestion
